/**
 * Believable per-field SAMPLE values shared by the two FE DataTable preview
 * surfaces so they paint identical rows:
 *   1. the live canvas component (`children/DataTable.tsx`), and
 *   2. the send/test-mail HTML preview marker expander
 *      (`lib/utils/expand-data-table-markers.ts`).
 *
 * Locale-formatted values (dates/times/statuses/booleans) are produced per
 * preview language in {@link sampleText}; the static maps below hold the
 * language-neutral (or English-fallback) samples.
 *
 * Data-bound tables (companions / appointments) have their real rows joined
 * from the payload at send time; both previews stand in three believable rows
 * keyed by the catalog `key` so the admin sees the final styled table rather
 * than placeholder chrome. Keep keys aligned with `data-table-catalog.ts`.
 */
import {
  formatApptDate,
  formatApptTime,
  humanizeAppointmentStatus,
} from "./appointment-record";
import { humanizeAcceptanceState } from "./companion-record";
import { localizedBoolean } from "@/lib/utils/additional-field-format";

export const SAMPLE_TEXT: Record<string, readonly string[]> = {
  firstName: ["Anna", "Lukas", "Sofia"],
  lastName: ["Schneider", "Becker", "Fischer"],
  email: [
    "anna.schneider@example.com",
    "lukas.becker@example.com",
    "sofia.fischer@example.com",
  ],
  phone: ["+49 30 123 456", "+49 30 654 321", "+49 30 987 654"],
  mobile: ["+49 151 2345 678", "+49 160 9876 543", "+49 152 4567 890"],
  company: ["Acme GmbH", "Globex AG", "Initech KG"],
  jobTitle: ["Marketing Lead", "Sales Manager", "Designer"],
  title: ["Dr.", "Prof.", "—"],
  gender: ["Female", "Male", "Female"],
  address: ["Hauptstraße 1", "Bahnhofstr. 12", "Lindenweg 7"],
  address2: ["c/o Acme", "Apt. 4B", "—"],
  zipCode: ["10115", "80331", "20095"],
  city: ["Berlin", "München", "Hamburg"],
  country: ["Deutschland", "Österreich", "Schweiz"],
  emailOptIn: ["Ja", "Nein", "Ja"],
  acceptanceState: ["Accepted", "Declined", "Pending"],
  language: ["Deutsch", "English", "Français"],
  portalLink: [
    "https://portal.veltric.app/p/anna",
    "https://portal.veltric.app/p/lukas",
    "https://portal.veltric.app/p/sofia",
  ],
  offeringName: ["Wellness Day", "Spa Retreat", "City Tour"],
  offeringDescription: ["Full-day package", "Weekend stay", "Guided 2h walk"],
  treatmentName: ["Deep Tissue Massage", "Facial Treatment", "Hot Stone"],
  treatmentDuration: ["60", "45", "90"],
  laneName: ["Room 1", "Room 2", "Studio A"],
  appointmentDate: ["12 Jun 2026", "13 Jun 2026", "14 Jun 2026"],
  appointmentTime: ["09:30", "14:00", "11:15"],
  appointmentEndTime: ["10:30", "14:45", "12:45"],
  appointmentDuration: ["60 min", "45 min", "90 min"],
  appointmentStatus: ["Confirmed", "Tentative", "Checked in"],
  appointmentNotes: ["First visit", "Allergy noted", "VIP guest"],
  cancelledReason: ["—", "—", "—"],
  sessionTime: ["09:30", "14:00", "11:15"],
  sessionEndTime: ["10:30", "14:45", "12:45"],
  sessionTitle: ["Opening Keynote", "Product Workshop", "Panel Discussion"],
  sessionLocation: ["Hall A", "Room 2", "Studio B"],
};

export const GENERIC_TEXT = ["Sample", "Example", "Demo"] as const;

// Fixed sample instants behind the appointment date/time samples — formatted
// per PREVIEW LANGUAGE below (a German template previews "12. Juni 2026",
// not the English "12 Jun 2026" these used to hardcode). UTC wall-clock is
// the displayed time, so rendering pins timeZone to UTC.
const SAMPLE_APPT_STARTS = [
  "2026-06-12T09:30:00Z",
  "2026-06-13T14:00:00Z",
  "2026-06-14T11:15:00Z",
] as const;
const SAMPLE_APPT_ENDS = [
  "2026-06-12T10:30:00Z",
  "2026-06-13T14:45:00Z",
  "2026-06-14T12:45:00Z",
] as const;
const SAMPLE_TZ = "UTC";
const SAMPLE_STATUSES = ["CONFIRMED", "TENTATIVE", "CHECKED_IN"] as const;
const SAMPLE_ACCEPTANCE = ["ACCEPTED", "DECLINED", "PENDING"] as const;
const SAMPLE_OPT_INS = [true, false, true] as const;

// Sample visuals for non-text columns (image / color) — a data-bound table has
// no live URLs/hex client-side, so both previews paint a believable swatch
// instead. Shared so the canvas (SampleCell) and the HTML preview render the
// same thing.
export const SAMPLE_COLORS = ["#6366f1", "#10b981", "#f59e0b"] as const;
export const SAMPLE_IMAGE_GRADIENTS = [
  "linear-gradient(135deg,#e0e7ff,#c7d2fe)",
  "linear-gradient(135deg,#d1fae5,#a7f3d0)",
  "linear-gradient(135deg,#fef3c7,#fde68a)",
] as const;

/** Number of believable sample rows previewed for a data-bound table. */
export const SAMPLE_ROW_COUNT = 3;

/**
 * Believable sample value for a column key at a given row index, localized to
 * `lang` where the REAL send-time value is locale-formatted (dates, times,
 * statuses, booleans — via the same helpers the real-row projections use, so
 * preview matches the inbox). Static text keys come from {@link SAMPLE_TEXT};
 * unknown keys fall back to {@link GENERIC_TEXT}. Omitting `lang` keeps the
 * historical English samples.
 */
export function sampleText(
  columnKey: string,
  rowIndex: number,
  lang?: string | null,
): string {
  if (lang) {
    const i = rowIndex % SAMPLE_ROW_COUNT;
    switch (columnKey) {
      case "appointmentDate":
        return formatApptDate(SAMPLE_APPT_STARTS[i], lang, SAMPLE_TZ);
      case "appointmentTime":
      // Session samples ride the same fixed instants so both data-bound
      // previews (appointments / sessions) format per preview language.
      case "sessionTime":
        return formatApptTime(SAMPLE_APPT_STARTS[i], lang, SAMPLE_TZ);
      case "appointmentEndTime":
      case "sessionEndTime":
        return formatApptTime(SAMPLE_APPT_ENDS[i], lang, SAMPLE_TZ);
      case "appointmentStatus":
        return humanizeAppointmentStatus(SAMPLE_STATUSES[i], lang);
      case "acceptanceState":
        return humanizeAcceptanceState(SAMPLE_ACCEPTANCE[i], lang);
      case "emailOptIn":
        return localizedBoolean(SAMPLE_OPT_INS[i]!, lang);
    }
  }
  const values = SAMPLE_TEXT[columnKey] ?? GENERIC_TEXT;
  return values[rowIndex % values.length] ?? "";
}

export function sampleColor(rowIndex: number): string {
  return SAMPLE_COLORS[rowIndex % SAMPLE_COLORS.length] ?? SAMPLE_COLORS[0];
}

export function sampleImageGradient(rowIndex: number): string {
  return SAMPLE_IMAGE_GRADIENTS[rowIndex % SAMPLE_IMAGE_GRADIENTS.length] ?? SAMPLE_IMAGE_GRADIENTS[0];
}
